// Common UI Components for Dashboard
import { useState } from "react";
import { format } from "date-fns";
import {
  Globe,
  Filter,
  Calendar,
  X,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { translations } from "@/i18n/translations";
import { PLATFORMS, getPlatformName } from "./constants";

// Language Selector Component
export const LanguageSelector = ({ lang, setLang }) => (
  <Select value={lang} onValueChange={setLang}>
    <SelectTrigger className="w-[88px] bg-card border-border/50 sm:w-[100px]" data-testid="language-selector">
      <Globe className="h-4 w-4 mr-1" />
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="en">English</SelectItem>
      <SelectItem value="ko">한국어</SelectItem>
      <SelectItem value="zh">中文</SelectItem>
    </SelectContent>
  </Select>
);

// Multi-Select Component for Platforms
export const PlatformMultiSelect = ({ selected = [], onChange, lang }) => {
  const t = translations[lang] || translations.en;
  const [open, setOpen] = useState(false);
  const platforms = PLATFORMS.filter(p => p.id !== "all");
  
  const safeSelected = selected || [];
  
  const togglePlatform = (platformId) => {
    // If currently "All" (empty array), select ALL EXCEPT the clicked one
    if (safeSelected.length === 0) {
      const allExceptClicked = platforms.filter(p => p.id !== platformId).map(p => p.id);
      onChange(allExceptClicked);
    } else if (safeSelected.includes(platformId)) {
      // If already selected, remove it
      const newSelection = safeSelected.filter(id => id !== platformId);
      // If removing makes it empty, return to "All" (empty array)
      onChange(newSelection.length === 0 ? [] : newSelection);
    } else {
      // Add to selection
      const newSelection = [...safeSelected, platformId];
      // If all platforms are now selected, return to "All" (empty array)
      if (newSelection.length === platforms.length) {
        onChange([]);
      } else {
        onChange(newSelection);
      }
    }
  };
  
  const selectAll = () => onChange([]);  // Empty = all selected
  
  const isAllSelected = safeSelected.length === 0;
  
  const getDisplayText = () => {
    if (isAllSelected) {
      return t.header.allPlatforms;
    }
    if (safeSelected.length === 1) {
      const platform = platforms.find(p => p.id === safeSelected[0]);
      return platform ? getPlatformName(platform, lang) : '1 Platform';
    }
    return `${safeSelected.length} ${t.header.platformsSelected}`;
  };
  
  const displayText = getDisplayText();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className="w-full max-w-full justify-between bg-card border-border/50 sm:w-[160px]" 
          data-testid="platform-multi-select"
        >
          <span className="truncate">{displayText}</span>
          <Filter className="h-4 w-4 ml-2 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(18rem,var(--radix-popover-trigger-width))] max-w-[calc(100vw-2rem)] p-2" align="start">
        <div className="flex justify-between mb-2 pb-2 border-b border-border/50">
          <Button variant="ghost" size="sm" onClick={selectAll} className="text-xs h-7">
            {t.header.allPlatforms}
          </Button>
        </div>
        <div className="space-y-1 max-h-[200px] overflow-y-auto">
          {platforms.map((platform) => {
            const isChecked = isAllSelected || safeSelected.includes(platform.id);
            return (
              <div
                key={platform.id}
                className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  togglePlatform(platform.id);
                }}
              >
                <Checkbox 
                  checked={isChecked}
                  className="pointer-events-none"
                />
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: platform.color }} />
                <span className="text-sm">{getPlatformName(platform, lang)}</span>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Multi-Select Component for Accounts
export const AccountMultiSelect = ({ accounts = [], selected, onChange, selectedPlatforms = [], lang }) => {
  const t = translations[lang] || translations.en;
  const [open, setOpen] = useState(false);
  
  const safeAccounts = accounts || [];
  const safeSelectedPlatforms = selectedPlatforms || [];
  const safeSelected = selected || [];
  
  const filteredAccounts = safeSelectedPlatforms.length === 0 
    ? safeAccounts 
    : safeAccounts.filter(a => safeSelectedPlatforms.includes(a.platform));
  
  const toggleAccount = (accountId) => {
    // If currently "All" (empty), switch to selecting ONLY the clicked account
    if (safeSelected.length === 0) {
      onChange([accountId]);
    } else if (safeSelected.includes(accountId)) {
      const newSelection = safeSelected.filter(id => id !== accountId);
      // If deselecting last one, return to "All"
      onChange(newSelection.length === 0 ? [] : newSelection);
    } else {
      onChange([...safeSelected, accountId]);
    }
  };
  
  const selectAll = () => onChange([]);  // Empty = all selected
  
  const isAllSelected = safeSelected.length === 0;
  
  const displayText = isAllSelected
    ? t.header.allAccounts 
    : safeSelected.length === 1
      ? filteredAccounts.find(a => a.id === safeSelected[0])?.display_name || '1 Account'
      : `${safeSelected.length} ${t.header.accountsSelected}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className="w-full max-w-full justify-between bg-card border-border/50 sm:w-[160px]" 
          data-testid="account-multi-select"
        >
          <span className="truncate">{displayText}</span>
          <Filter className="h-4 w-4 ml-2 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(20rem,var(--radix-popover-trigger-width))] max-w-[calc(100vw-2rem)] p-2" align="start">
        <div className="flex justify-between mb-2 pb-2 border-b border-border/50">
          <Button variant="ghost" size="sm" onClick={selectAll} className="text-xs h-7">
            {t.header.allAccounts}
          </Button>
        </div>
        <div className="space-y-1 max-h-[250px] overflow-y-auto">
          {filteredAccounts.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No accounts</p>
          ) : (
            filteredAccounts.map((account) => {
              const platformColor = PLATFORMS.find(p => p.id === account.platform)?.color;
              const isChecked = isAllSelected || safeSelected.includes(account.id);
              return (
                <div
                  key={account.id}
                  className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 cursor-pointer"
                  onClick={() => toggleAccount(account.id)}
                >
                  <Checkbox 
                    checked={isChecked}
                    className="pointer-events-none"
                  />
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: platformColor }} />
                  <div className="flex flex-col">
                    <span className="text-sm">{account.display_name}</span>
                    <span className="text-xs text-muted-foreground capitalize">{account.platform}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Metric Card Component
export const MetricCard = ({ title, value, icon: Icon, trend, subtext, color, noData }) => (
  <Card className="metric-card bg-card border-border/50" data-testid={`metric-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
      <CardTitle className="text-[10px] sm:text-sm font-medium text-muted-foreground font-mono uppercase tracking-wider leading-tight">
        {title}
      </CardTitle>
      <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground shrink-0" style={{ color }} />
    </CardHeader>
    <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
      <div className="text-xl sm:text-3xl font-bold font-mono tracking-tight text-foreground">{value}</div>
      {trend !== undefined && trend !== null ? (
        <p className="flex items-center gap-1 text-[10px] sm:text-xs text-muted-foreground mt-1">
          {trend >= 0 ? <TrendingUp className="h-3 w-3 text-emerald-500 shrink-0" /> : <TrendingDown className="h-3 w-3 text-red-500 shrink-0" />}
          <span className={trend >= 0 ? "text-emerald-500" : "text-red-500"}>
            {trend >= 0 ? "+" : ""}{trend}%
          </span>
          <span className="text-muted-foreground/70 truncate">{subtext}</span>
        </p>
      ) : noData ? (
        <p className="flex items-center gap-1 text-[10px] sm:text-xs text-muted-foreground/50 mt-1">
          <span>—</span>
          <span className="truncate">{subtext}</span>
        </p>
      ) : null}
    </CardContent>
  </Card>
);

// Date Range Picker Component
export const DateRangePicker = ({ 
  startDate, 
  endDate, 
  onStartChange, 
  onEndChange,
  // Support alternative prop names
  onStartDateChange,
  onEndDateChange,
}) => {
  // Use either prop name
  const handleStartChange = onStartChange || onStartDateChange;
  const handleEndChange = onEndChange || onEndDateChange;
  
  return (
    <div className="grid w-full grid-cols-1 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center sm:gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full min-w-0 justify-start text-left font-normal border-border/50 text-xs sm:w-[130px] sm:text-sm" data-testid="start-date-picker">
            <Calendar className="mr-1 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
            {startDate ? format(startDate, "MMM dd") : "Start"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <CalendarComponent
            mode="single"
            selected={startDate}
            onSelect={handleStartChange}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      <span className="hidden text-muted-foreground text-xs sm:inline">~</span>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full min-w-0 justify-start text-left font-normal border-border/50 text-xs sm:w-[130px] sm:text-sm" data-testid="end-date-picker">
            <Calendar className="mr-1 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
            {endDate ? format(endDate, "MMM dd") : "End"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <CalendarComponent
            mode="single"
            selected={endDate}
            onSelect={handleEndChange}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {(startDate || endDate) && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { handleStartChange(null); handleEndChange(null); }}
          className="h-8 w-full p-0 text-muted-foreground hover:text-foreground sm:w-8"
          data-testid="clear-dates-btn"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};
